import { DocumentComponents, DocumentNode, DocumentPath } from '../types';
import { createId } from './createId';

export function findNode(
  node: DocumentNode,
  path: DocumentPath,
): DocumentNode | null {
  let current = node;
  for (let i = 0; i < path.length; i += 1) {
    current = current.children[path[i]];
    if (current == null) { return null; }
  }
  return current;
}

export function findAncestorNodes(
  document: DocumentNode,
  path: DocumentPath,
): DocumentNode[] {
  if (path.length === 0) { return []; }
  return [
    findNode(document, []),
    ...path.map((_, i) => findNode(document, path.slice(0, i + 1))),
  ].filter((s) => !!s) as DocumentNode[];
}

export function setNode(
  node: DocumentNode,
  path: DocumentPath,
  value: DocumentNode,
): DocumentNode {
  if (path.length === 0) {
    return value;
  }
  return {
    ...node,
    children: node.children.map((child, i) => {
      if (i === path[0]) {
        return setNode(child, path.slice(1), value);
      }
      return child;
    }),
  };
}

export function removeNode(
  node: DocumentNode,
  path: DocumentPath,
): DocumentNode {
  if (path.length === 1) {
    return {
      ...node,
      children: node.children.filter((_, i) => i !== path[0]),
    };
  }
  return {
    ...node,
    children: node.children.map((child, i) => {
      if (i === path[0]) {
        return removeNode(child, path.slice(1));
      }
      return child;
    }),
  };
}

export function insertNode(
  node: DocumentNode,
  path: DocumentPath,
  value: DocumentNode,
): DocumentNode {
  if (path.length === 1) {
    return {
      ...node,
      children: [
        ...node.children.slice(0, path[0]),
        {
          ...value,
          children: value.children.map((child) => (
            { ...child, key: child.key ?? createId() }
          )),
        },
        ...node.children.slice(path[0]),
      ],
    };
  }
  return {
    ...node,
    children: node.children.map((child, i) => {
      if (i === path[0]) {
        return insertNode(child, path.slice(1), value);
      }
      return child;
    }),
  };
}

export function canInsertNode(
  components: DocumentComponents,
  document: DocumentNode,
  path: DocumentPath,
  value: DocumentNode,
): boolean {
  const parent = findNode(document, path.slice(0, -1));
  const ancestors = findAncestorNodes(document, path).map(({ type }) => type);
  if (parent == null) { return false; }
  const ParentComponent = components[parent.type];
  const ChildComponent = components[value.type];
  const isChildAllowed =
    ParentComponent.allowedChildren == null ||
    ParentComponent.allowedChildren.includes(value.type);
  const isParentAllowed =
    ChildComponent.allowedParent == null ||
    ChildComponent.allowedParent.includes(parent.type);
  const isAncestorAllowed =
    ChildComponent.disallowedAncestors == null ||
    !ChildComponent.disallowedAncestors.some(
      (a) => ancestors.indexOf(a) !== -1,
    );
  return isChildAllowed && isParentAllowed && isAncestorAllowed;
}

export function canMoveNode(
  components: DocumentComponents,
  document: DocumentNode,
  srcPath: DocumentPath,
  destPath: DocumentPath,
): boolean {
  const src = findNode(document, srcPath);
  if (src == null) { return false; }
  // Check the validity of the movement; For example, we can't ever move
  // the parent into the children.
  const isParent =
    srcPath.length <= destPath.length &&
    srcPath.every((v, i) => destPath[i] === v);
  if (isParent) { return false; }
  return canInsertNode(components, document, destPath, src);
}

export function moveNode(
  document: DocumentNode,
  srcPath: DocumentPath,
  destPath: DocumentPath,
): { document: DocumentNode, cursor: DocumentPath } {
  // Remove (pluck) the node and try to store it into the destination.
  const srcNode = findNode(document, srcPath);
  if (srcNode == null) {
    return { document, cursor: destPath };
  }
  const removedDocument = removeNode(document, srcPath);
  // Re-insert the document to target.
  // But wait! If the srcPath and destPath sufficiently colides, the destPath
  // must be decreased by 1.
  // For example,
  // - A [0]
  //   - B [0, 0]
  //   - C [0, 1]
  //     - D [0, 1, 0]
  // Suppose that B [0, 0] is being moved inside C [0, 1, 0]. In the insertion
  // time, the B node would be removed from the document, therefore
  // C's offset will be changed to [0, 0].
  let destFixedPath = destPath;
  // It only happens if the src node is shallower than the dest node.
  if (
    srcPath.length <= destPath.length &&
    srcPath.every((v, i) => {
      if (i < srcPath.length - 1) {
        return v === destPath[i];
      }
      return v < destPath[i];
    })
  ) {
    // Decrease the offending offset by 1.
    destFixedPath = destPath.map((v, i) => {
      if (i === srcPath.length - 1) {
        return v - 1;
      }
      return v;
    });
  }
  // Insert the node in the position.
  return {
    document: insertNode(removedDocument, destFixedPath, srcNode),
    cursor: destFixedPath,
  };
}

export function cloneNode(node: DocumentNode, removeKey = false): DocumentNode {
  return {
    key: removeKey ? null : createId(),
    type: node.type,
    props: node.props,
    children: node.children.map((child) => cloneNode(child, removeKey)),
  };
}
